import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import { startOfWeek, endOfWeek, addWeeks, subWeeks, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { DayView } from "@/components/schedule/DayView";
import { WeekView } from "@/components/schedule/WeekView";
import { ShiftEditDialog } from "@/components/schedule/ShiftEditDialog";
import { CreateShiftDialog } from "@/components/schedule/CreateShiftDialog";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useHasPermission } from "@/hooks/permissions/useHasPermission";
import { useCompanyShifts, useCompanyProfiles, useCreateShift, useEditShift, useCancelShift, scheduleKeys } from "@/hooks/schedule";
import { useCompanyLeaveOverlay } from "@/hooks/leave";
import { usePublicHolidayOverlay } from "@/hooks/usePublicHolidayOverlay";
import { useCompanyTimezone } from "@/hooks/useCompanyTimezone";
import { useQueryClient } from "@tanstack/react-query";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
}

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  status?: 'active' | 'open';
  employee_name: string;
  offered_by_user_id?: string | null; // NEW
  offered_by_name?: string | null; // NEW
}

export default function SchedulePlanner() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // View state
  const [viewMode, setViewMode] = useState<'day' | 'week'>(isMobile ? 'day' : 'week');
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // React Query for data fetching
  const queryClient = useQueryClient();
  const companyTimezone = useCompanyTimezone();
  
  // Derived values for effects - consolidate date management
  const companyId = profile?.company_id;
  
  // Derive both selectedDate and currentWeek from single source of truth
  const selectedDate = useMemo(() => {
    return viewMode === 'day' ? currentDate : startOfWeek(currentDate);
  }, [currentDate, viewMode]);
  
  const currentWeek = useMemo(() => {
    return viewMode === 'week' ? currentDate : startOfWeek(currentDate);
  }, [currentDate, viewMode]);

  // React Query hooks for data fetching
  const { 
    data: employees = [], 
    isLoading: employeesLoading,
    error: employeesError 
  } = useCompanyProfiles(companyId);
  
  // Filter to only active employees for dropdowns (exclude archived)
  // Keep full `employees` list for shift enrichment (showing names on existing shifts)
  const activeEmployees = useMemo(() => 
    employees.filter(e => e.status === 'active' || e.status === null),
    [employees]
  );
  
  const { 
    data: shifts = [], 
    isLoading: shiftsLoading,
    error: shiftsError
  } = useCompanyShifts(companyId, currentWeek, employees);
  
  // Fetch approved leave overlays for the visible date range
  const weekStartStr = format(startOfWeek(currentWeek), 'yyyy-MM-dd');
  const weekEndStr = format(endOfWeek(currentWeek), 'yyyy-MM-dd');
  
  const { 
    data: leaveOverlays = [],
  } = useCompanyLeaveOverlay(companyId, weekStartStr, weekEndStr);
  
  // Public holiday overlays for the visible week
  const { holidays: publicHolidays } = usePublicHolidayOverlay(weekStartStr, weekEndStr);
  
  const loading = employeesLoading || shiftsLoading;
  const enrichedShifts = shifts; // Already enriched in the hook

  // Edit shift state
  const [editShift, setEditShift] = useState<Shift | null>(null);
  const editShiftOpen = !!editShift;

  // Create shift context state
  type CreateCtx = { 
    context: "empty-week" | "cell" | "toolbar"; 
    weekStartISO?: string; 
    weekEndISO?: string; 
    initialDateISO?: string; 
    initialStartHHmm?: string;
    initialEndHHmm?: string;
  };
  const [createCtx, setCreateCtx] = useState<CreateCtx | null>(null);

  // React Query mutation hooks
  const createShiftMutation = useCreateShift(companyId, currentWeek, employees);
  const editShiftMutation = useEditShift(companyId, currentWeek, employees);
  const cancelShiftMutation = useCancelShift(companyId, currentWeek);

  // User preferences
  const { confirmShiftCancel, setConfirmShiftCancel } = useUserPreferences();

  // Permission-based access: can create open shifts
  const canEdit = useHasPermission('add_edit_shifts');
  const allowOpenShifts = canEdit;

  // Auth check effect - route protection via ProtectedRoute handles permission check
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    // Permission check is now handled by ProtectedRoute with requiredPermissions
  }, [user, navigate]);

  // Error handling for React Query
  useEffect(() => {
    if (employeesError) {
      console.error('Error fetching employees:', employeesError);
      // Don't show error toast here - React Query will retry automatically
    }
  }, [employeesError]);

  useEffect(() => {
    if (shiftsError) {
      console.error('Error fetching shifts:', shiftsError);
      // Don't show error toast here - React Query will retry automatically
    }
  }, [shiftsError]);

  // Add offer handler that uses React Query cache invalidation
  const handleShiftOffered = useCallback(async (shiftId: string) => {
    console.log('[SchedulePlanner] Shift offered, refreshing data:', shiftId);
    
    // Invalidate and refetch company shifts
    const weekStartISO = startOfWeek(currentWeek).toISOString();
    const weekEndISO = endOfWeek(currentWeek).toISOString();
    
    await queryClient.invalidateQueries({
      queryKey: scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO)
    });
  }, [queryClient, companyId, currentWeek]);

  // Prefetch adjacent weeks for better performance
  useEffect(() => {
    if (!companyId) return;
    
    // Prefetch next week
    const nextWeek = addWeeks(currentWeek, 1);
    const nextWeekStart = startOfWeek(nextWeek).toISOString();
    const nextWeekEnd = endOfWeek(nextWeek).toISOString();
    
    queryClient.prefetchQuery({
      queryKey: scheduleKeys.companyShifts(companyId, nextWeekStart, nextWeekEnd),
      queryFn: async () => {
        const { fetchCompanyShifts } = await import('@/services/schedule/shiftService');
        return fetchCompanyShifts(companyId, nextWeekStart, nextWeekEnd);
      },
      staleTime: 2 * 60 * 1000,
    });
    
    // Prefetch previous week
    const prevWeek = subWeeks(currentWeek, 1);
    const prevWeekStart = startOfWeek(prevWeek).toISOString();
    const prevWeekEnd = endOfWeek(prevWeek).toISOString();
    
    queryClient.prefetchQuery({
      queryKey: scheduleKeys.companyShifts(companyId, prevWeekStart, prevWeekEnd),
      queryFn: async () => {
        const { fetchCompanyShifts } = await import('@/services/schedule/shiftService');
        return fetchCompanyShifts(companyId, prevWeekStart, prevWeekEnd);
      },
      staleTime: 2 * 60 * 1000,
    });
  }, [companyId, currentWeek, queryClient]);

  const openCreate = (ctx: CreateCtx) => setCreateCtx(ctx);

  const handleCreateFromEmptyWeek = async ({ weekStartISO, weekEndISO }: { weekStartISO: string; weekEndISO: string }) => {
    if (!companyId) return;

    // React Query will handle loading employees automatically
    setCreateCtx({ context: "empty-week", weekStartISO, weekEndISO });
  };

  const handleTimeSlotClick = (date: Date, hour?: number) => {
    openCreate({
      context: "cell",
      initialDateISO: date.toISOString(),
    });
  };

  const handleCreateShift = (date: Date) => {
    setCurrentDate(date);
    openCreate({
      context: "cell",
      initialDateISO: date.toISOString(),
    });
  };

  // Shift click handler for editing
  const handleShiftClick = (shift: Shift) => {
    setEditShift(shift);
  };

  // Edit shift handlers - React Query will handle cache updates
  const handleSaveShift = async (args: { profile_id: string; start: Date; end: Date; note: string | null }) => {
    if (!editShift) return;

    editShiftMutation.mutate({
      shiftId: editShift.id,
      profileId: args.profile_id,
      start: args.start,
      end: args.end,
      note: args.note,
    });
    
    // Close the edit dialog after mutation starts
    setEditShift(null);
  };

  const handleCancelShift = async (shiftId: string) => {
    cancelShiftMutation.mutate(shiftId);
    
    // Close the edit dialog after mutation starts
    setEditShift(null);
  };

  const handleSubmitShift = async (args: {
    profile_id: string;
    start_time: string;
    end_time: string;
    note: string | null;
  }) => {
    // Convert profile_id to null if it's empty string (for open shifts)
    const profile_id = args.profile_id === '' ? null : args.profile_id;
    
    createShiftMutation.mutate({
      profile_id,
      start_time: args.start_time,
      end_time: args.end_time,
      note: args.note,
    });
    
    // Close the create dialog after mutation starts
    setCreateCtx(null);
  };

  // Note: Removed problematic useEffect hooks that caused infinite render loop
  // Date synchronization is now handled through derived values from currentDate

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 md:py-8 space-y-4 md:space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 md:h-6 md:w-6" />
          <h1 className="text-xl md:text-2xl font-bold">Schedule Planner</h1>
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'day' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('day')}
            className="flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            Day
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('week')}
            className="flex items-center gap-2"
          >
            <Clock className="h-4 w-4" />
            Week
          </Button>
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'day' ? (
        <DayView
          selectedDate={selectedDate}
          onDateChange={setCurrentDate}
          shifts={enrichedShifts}
          onCreateShift={handleCreateShift}
          onShiftClick={handleShiftClick}
          leaveOverlays={leaveOverlays}
          publicHolidays={publicHolidays}
          companyTimezone={companyTimezone}
        />
      ) : (
        <WeekView
          currentWeek={currentWeek}
          onWeekChange={setCurrentDate}
          shifts={enrichedShifts}
          onTimeSlotClick={handleTimeSlotClick}
          onShiftsUpdated={() => {}} // No-op since React Query handles updates
          profiles={employees}
          onShiftClick={handleShiftClick}
          leaveOverlays={leaveOverlays}
          publicHolidays={publicHolidays}
          companyTimezone={companyTimezone}
          onCreateShift={(context, weekStartISO, weekEndISO, initialDateISO, initialStartHHmm, initialEndHHmm) => {
            if (context === "empty-week") {
              handleCreateFromEmptyWeek({ weekStartISO: weekStartISO!, weekEndISO: weekEndISO! });
            } else if (context === "cell") {
              setCreateCtx({
                context: "cell",
                initialDateISO,
                initialStartHHmm,
                initialEndHHmm,
              });
            }
          }}
        />
      )}

      {/* Create Shift Dialog */}
      {createCtx && (
        <CreateShiftDialog
          key={[
            createCtx.context,
            createCtx.initialDateISO ?? "",
            createCtx.initialStartHHmm ?? "",
            createCtx.initialEndHHmm ?? "",
            createCtx.weekStartISO ?? "",
            createCtx.weekEndISO ?? "",
          ].join("|")}
          open={!!createCtx}
          onOpenChange={(open) => !open && setCreateCtx(null)}
          context={createCtx.context}
          weekStartISO={createCtx.weekStartISO}
          weekEndISO={createCtx.weekEndISO}
          initialDateISO={createCtx.initialDateISO}
          initialStartHHmm={createCtx.initialStartHHmm}
          initialEndHHmm={createCtx.initialEndHHmm}
          profiles={activeEmployees}
          defaultStart="09:00"
          defaultEnd="10:00"
          allowOpenShifts={allowOpenShifts}
          existingShifts={enrichedShifts}
          onCreate={handleSubmitShift}
        />
      )}

      {/* Shift Edit Dialog */}
      <ShiftEditDialog
        open={editShiftOpen}
        onOpenChange={(open) => { if (!open) setEditShift(null); }}
        shift={editShift}
        profiles={activeEmployees}
        existingShifts={enrichedShifts}
        onShiftUpdated={async (updatedShift) => {
          // React Query will handle cache updates automatically
        }}
        onShiftCancelled={async (shiftId) => {
          // React Query will handle cache updates automatically
        }}
        onShiftOffered={handleShiftOffered}
        cancelShiftMutation={cancelShiftMutation}
        editShiftMutation={editShiftMutation}
        confirmBeforeCancel={confirmShiftCancel}
        onChangeConfirmBeforeCancel={setConfirmShiftCancel}
      />
    </div>
  );
}
